import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Eye, EyeOff, Image, Calendar, Tag } from 'lucide-react';
import { API } from '../../utils/database-frontend.js';
import EditModal from './EditModal';
import DeleteConfirmModal from './DeleteConfirmModal';

const ContentManager = ({ contentType, onSuccess, onError }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);

  const contentConfig = {
    products: {
      title: 'Ürünler',
      api: API.products,
      fields: ['title', 'description', 'image_url'],
      displayFields: ['title', 'description'],
      hasVisibility: true
    },
    services: {
      title: 'Hizmetler', 
      api: API.services,
      fields: ['title', 'description', 'image_url'],
      displayFields: ['title', 'description'],
      hasVisibility: true
    },
    references: {
      title: 'Referanslar',
      api: API.references,
      fields: ['image_url', 'alt_text', 'is_featured', 'is_active'],
      displayFields: ['alt_text'],
      hasVisibility: true
    },
    news: {
      title: 'Haberler',
      api: API.news,
      fields: ['title', 'description', 'image_url', 'published_date'],
      displayFields: ['title', 'description', 'published_date'],
      hasVisibility: true
    },
    gallery: {
      title: 'Galeri',
      api: API.gallery,
      fields: ['image_url', 'alt_text', 'category', 'is_featured'],
      displayFields: ['alt_text', 'category'],
      hasVisibility: true
    },
    documents: {
      title: 'Belgelerimiz',
      api: API.documents,
      fields: ['title', 'description', 'image_url'],
      displayFields: ['title', 'description'],
      hasVisibility: true
    }
  };

  const config = contentConfig[contentType];

  // Fetch items from database
  useEffect(() => {
    fetchItems();
  }, [contentType]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      // Admin panel için tüm içerikleri getir (aktif/pasif dahil)
      let fetchedItems;
      if (contentType === 'products' || contentType === 'services' || contentType === 'references') {
        fetchedItems = await config.api.getAll(true); // includeInactive = true
      } else if (contentType === 'news') {
        fetchedItems = await config.api.getAll(true); // includeUnpublished = true
      } else {
        fetchedItems = await config.api.getAll(); // gallery zaten filtre yapmıyor
      }
      setItems(fetchedItems);
    } catch (error) {
      console.error(`Error fetching ${contentType}:`, error);
      onError(`❌ ${config.title} yüklenirken hata oluştu.`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
  };

  const handleDelete = (item) => {
    setDeletingItem(item);
  };

  const handleToggleVisibility = async (item) => {
    try {
      const field = contentType === 'news' ? 'is_published' : 
                   contentType === 'gallery' ? 'is_featured' : 
                   'is_active'; // products, services, references
      
      const updatedData = {
        ...item,
        [field]: !item[field]
      };

      await config.api.update(item.id, updatedData);
      
      // Update local state
      setItems(items.map(i => i.id === item.id ? updatedData : i));
      
      const status = updatedData[field] ? 'aktif' : 'pasif';
      onSuccess(`✅ İçerik ${status} olarak işaretlendi.`);
    } catch (error) {
      console.error('Error toggling visibility:', error);
      onError('❌ Görünürlük değiştirilirken hata oluştu.');
    }
  };

  const handleEditSuccess = (updatedItem) => {
    setItems(items.map(item => item.id === updatedItem.id ? updatedItem : item));
    setEditingItem(null);
    onSuccess('✅ İçerik başarıyla güncellendi.');
  };

  const handleDeleteSuccess = (deletedId) => {
    setItems(items.filter(item => item.id !== deletedId));
    setDeletingItem(null);
    onSuccess('✅ İçerik başarıyla silindi.');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      let date;
      if (typeof dateString === 'string') {
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          date = new Date(dateString + 'T00:00:00');
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date in ContentManager:', dateString);
        return 'Geçersiz tarih';
      }
      
      return date.toLocaleDateString('tr-TR');
    } catch (error) {
      console.error('Date formatting error in ContentManager:', error, dateString);
      return 'Tarih hatası';
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getVisibilityStatus = (item) => {
    if (contentType === 'news') return item.is_published;
    if (contentType === 'gallery') return item.is_featured;
    return item.is_active !== false; // Default to true if field doesn't exist (products, services, references)
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Yükleniyor...</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">📭</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz {config.title.toLowerCase()} yok</h3>
        <p className="text-gray-600">Yeni içerik eklemek için "Yeni Ekle" sekmesini kullanın.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">
          {config.title} ({items.length})
        </h2>
      </div>

      {/* Content Grid/List */}
      <div className="grid grid-cols-1 gap-6">
        {items.map((item) => (
          <div key={item.id} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between">
                {/* Content Preview */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start space-x-4">
                    {/* Image Preview */}
                    {(item.imageUrl || item.image_url) && (
                      <div className="flex-shrink-0">
                        <img
                          src={item.imageUrl || item.image_url}
                          alt={item.title || item.alt_text || 'Preview'}
                          className="w-16 h-16 rounded-lg object-cover bg-gray-100"
                          onError={(e) => {
                            e.target.src = 'https://placehold.co/64x64/e5e7eb/9ca3af?text=?';
                          }}
                        />
                      </div>
                    )}

                    {/* Content Details */}
                    <div className="flex-1 min-w-0">
                      {/* Title */}
                      {item.title && (
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {truncateText(item.title, 80)}
                        </h3>
                      )}

                      {/* Description */}
                      {item.description && (
                        <p className="text-gray-600 text-sm mb-2">
                          {truncateText(item.description, 150)}
                        </p>
                      )}

                      {/* Alt Text for References/Gallery */}
                      {item.alt_text && !item.title && (
                        <p className="text-gray-700 font-medium mb-2">
                          {truncateText(item.alt_text, 80)}
                        </p>
                      )}

                      {/* Metadata */}
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        {/* Date */}
                        {(item.publishedDate || item.published_date) && (
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(item.publishedDate || item.published_date)}</span>
                          </div>
                        )}

                        {/* Category */}
                        {item.category && (
                          <div className="flex items-center space-x-1">
                            <Tag className="w-3 h-3" />
                            <span className="capitalize">{item.category}</span>
                          </div>
                        )}

                        {/* Created Date */}
                        <div className="flex items-center space-x-1">
                          <span>Oluşturma: {formatDate(item.createdAt || item.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 ml-4">
                  {/* Visibility Toggle */}
                  {config.hasVisibility && (
                    <button
                      onClick={() => handleToggleVisibility(item)}
                      className={`p-2 rounded-lg transition-colors ${
                        getVisibilityStatus(item)
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                      title={getVisibilityStatus(item) ? 'Yayında' : 'Pasif'}
                    >
                      {getVisibilityStatus(item) ? (
                        <Eye className="w-4 h-4" />
                      ) : (
                        <EyeOff className="w-4 h-4" />
                      )}
                    </button>
                  )}

                  {/* Edit Button */}
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    title="Düzenle"
                  >
                    <Edit className="w-4 h-4" />
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDelete(item)}
                    className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                    title="Sil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Status Badge */}
              <div className="mt-4 flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    getVisibilityStatus(item)
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {getVisibilityStatus(item) ? 'Yayında' : 'Pasif'}
                  </span>

                  {/* Featured Badge */}
                  {(contentType === 'references' || contentType === 'gallery') && item.is_featured && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Öne Çıkan
                    </span>
                  )}
                </div>

                <div className="text-xs text-gray-400">
                  ID: {item.id}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editingItem && (
        <EditModal
          item={editingItem}
          contentType={contentType}
          config={config}
          onClose={() => setEditingItem(null)}
          onSuccess={handleEditSuccess}
          onError={onError}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingItem && (
        <DeleteConfirmModal
          item={deletingItem}
          contentType={contentType}
          config={config}
          onClose={() => setDeletingItem(null)}
          onSuccess={handleDeleteSuccess}
          onError={onError}
        />
      )}
    </div>
  );
};

export default ContentManager; 